import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Drawer, Card, Button, Icon, Input, FormField, Textarea, EmptyState, Badge,
} from './ui';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeHtml } from '../utils/sanitize';
import { API_URL } from '../config/api';

/**
 * Header drawer that shows the internal release-notes feed.
 *
 * Read flow:
 *   - GET /api/changelog returns {posts, unreadCount}; each post
 *     carries isRead computed from the caller's UserChangelogRead rows.
 *   - As soon as the drawer mounts we fire POST /mark-all-read so the
 *     header badge clears the next time it polls. We keep the visual
 *     "חדש" pill on the row that *was* unread when we opened, so the
 *     user can tell at a glance which posts are new — even though
 *     they're now technically marked-read.
 *
 * Write flow (super-admin):
 *   - A "פוסט חדש" button reveals an inline form. Body is a plain
 *     textarea but supports a simple markdown subset rendered with
 *     a tiny converter (paragraphs + emojis + line breaks). Keeping
 *     it plain because nobody on the team wants a markdown cheat-
 *     sheet inside a release-notes drawer — we just want short
 *     human paragraphs.
 */

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface ChangelogPost {
  id: string;
  title: string;
  emoji?: string | null;
  category?: string | null;
  body: string;
  isPublished: boolean;
  publishedAt: string;
  author: { id: string; name: string } | null;
  isRead?: boolean;
  createdAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after mark-all-read succeeds so the header can drop its badge. */
  onMarkedRead?: () => void;
}

const COMMON_EMOJIS = ['✨', '🚀', '🐛', '🔧', '📨', '📞', '💬', '🎯', '⚡', '🔒', '📊', '🎨'];

const CATEGORIES = [
  { value: 'feature',     label: 'פיצ׳ר חדש',  color: 'bg-emerald-50 text-emerald-700' },
  { value: 'improvement', label: 'שיפור',       color: 'bg-blue-50 text-blue-700' },
  { value: 'fix',         label: 'תיקון',        color: 'bg-amber-50 text-amber-700' },
  { value: 'security',    label: 'אבטחה',        color: 'bg-purple-50 text-purple-700' },
];

const catStyle = (cat?: string | null) => {
  const found = CATEGORIES.find(c => c.value === cat);
  return found?.color || 'bg-slate-100 text-slate-700';
};
const catLabel = (cat?: string | null) => {
  const found = CATEGORIES.find(c => c.value === cat);
  return found?.label || cat;
};

// Tiny markdown-ish renderer: paragraphs separated by blank lines,
// **bold**, line breaks. Output is sanitized before render.
function renderBody(md: string): string {
  const escaped = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const withBold = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const paragraphs = withBold.split(/\n\s*\n/).map(p => {
    const inner = p.replace(/\n/g, '<br/>').trim();
    return inner ? `<p>${inner}</p>` : '';
  }).join('');
  return paragraphs;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(1, Math.round((now - then) / 1000));
  if (sec < 60)    return `לפני ${sec} שניות`;
  const min = Math.round(sec / 60);
  if (min < 60)    return `לפני ${min} דקות`;
  const hr = Math.round(min / 60);
  if (hr < 24)     return `לפני ${hr} שעות`;
  const days = Math.round(hr / 24);
  if (days < 30)   return `לפני ${days} ימים`;
  const months = Math.round(days / 30);
  if (months < 12) return `לפני ${months} חודשים`;
  return new Date(iso).toLocaleDateString('he-IL');
}

const ChangelogDrawer: React.FC<Props> = ({ open, onClose, onMarkedRead }) => {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();
  const [posts, setPosts] = useState<ChangelogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState({ title: '', body: '', emoji: '✨', category: 'feature' });
  const [posting, setPosting] = useState(false);
  // Capture which ids were unread *when we opened* — used to keep the
  // "חדש" pill visible after mark-all-read flips isRead to true.
  const [openTimeUnreadIds, setOpenTimeUnreadIds] = useState<Set<string>>(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setComposerOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/changelog`, { headers: authHeaders() })
      .then(r => {
        if (cancelled) return;
        const list: ChangelogPost[] = r.data?.posts || [];
        setPosts(list);
        setOpenTimeUnreadIds(new Set(list.filter(p => !p.isRead).map(p => p.id)));
        // Best-effort mark-all-read; failures don't matter — next open re-tries.
        if (list.some(p => !p.isRead)) {
          axios.post(`${API_URL}/api/changelog/mark-all-read`, {}, { headers: authHeaders() })
            .then(() => onMarkedRead?.())
            .catch(() => {});
        }
      })
      .catch(() => toast.error('שגיאה בטעינת היומן'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePost = async () => {
    if (!draft.title.trim() || !draft.body.trim()) {
      toast.error('כותרת וגוף הם חובה');
      return;
    }
    setPosting(true);
    try {
      const r = await axios.post(`${API_URL}/api/changelog`, {
        title: draft.title.trim(),
        body: draft.body,
        emoji: draft.emoji || null,
        category: draft.category || null,
      }, { headers: authHeaders() });
      const created: ChangelogPost = r.data?.post;
      setPosts(prev => [{ ...created, isRead: true }, ...prev]);
      setDraft({ title: '', body: '', emoji: '✨', category: 'feature' });
      setComposerOpen(false);
      toast.success('הפוסט פורסם');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'פרסום נכשל');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${API_URL}/api/changelog/${id}`, { headers: authHeaders() });
      setPosts(prev => prev.filter(p => p.id !== id));
      toast.success('הפוסט נמחק');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'מחיקה נכשלה');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const visible = useMemo(() => posts.filter(p => p.isPublished), [posts]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="מה חדש במערכת"
      subtitle="יומן עדכונים — לכל הצוות"
      size="lg"
    >
      {isSuperAdmin && (
        <div className="mb-3">
          {!composerOpen ? (
            <Button variant="primary" icon="add" onClick={() => setComposerOpen(true)}>
              פוסט חדש
            </Button>
          ) : (
            <Card padding="sm" className="bg-slate-50/70 border-2 border-dashed border-slate-300">
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <FormField label="" hint="">
                    <div className="flex items-center gap-1 flex-wrap">
                      {COMMON_EMOJIS.map(e => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => setDraft(d => ({ ...d, emoji: e }))}
                          className={[
                            'size-8 rounded-lg text-lg transition-all',
                            draft.emoji === e
                              ? 'bg-primary/10 ring-2 ring-primary scale-110'
                              : 'hover:bg-slate-100',
                          ].join(' ')}
                          title={e}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </FormField>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <FormField label="קטגוריה">
                    <select
                      value={draft.category}
                      onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                    >
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </FormField>
                  <FormField label="כותרת" required>
                    <Input
                      value={draft.title}
                      onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                      placeholder="לדוגמה: שליחת הצעות בוואטאפ"
                    />
                  </FormField>
                </div>

                <FormField
                  label="תוכן"
                  required
                  hint="פסקה ריקה = פסקה חדשה. **טקסט** = מודגש."
                >
                  <Textarea
                    value={draft.body}
                    onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
                    rows={6}
                    dir="rtl"
                    placeholder={'מה חדש?\n\nתאר את הפיצ׳ר בשפה של צוות לא טכני.\n\nאיך משתמשים בו?'}
                  />
                </FormField>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button variant="secondary" onClick={() => setComposerOpen(false)} disabled={posting}>
                    ביטול
                  </Button>
                  <Button variant="primary" icon="send" onClick={handlePost} loading={posting}>
                    פרסם
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-slate-500 text-sm">
          <Icon name="progress_activity" size={20} className="animate-spin mx-auto mb-2" />
          טוען...
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon="campaign"
          title="עוד אין עדכונים"
          description={isSuperAdmin ? 'פרסם פוסט ראשון כדי שהצוות ידע על שינויים חדשים' : 'כל עדכון חדש למערכת יופיע כאן'}
        />
      ) : (
        <div className="space-y-3">
          {visible.map(p => {
            const wasUnread = openTimeUnreadIds.has(p.id);
            return (
              <Card key={p.id} padding="md" className={wasUnread ? 'ring-2 ring-primary/20' : ''}>
                <div className="flex items-start gap-3">
                  <div className="text-2xl flex-shrink-0 leading-none mt-0.5">
                    {p.emoji || '📣'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-slate-900 text-sm">{p.title}</h3>
                      {p.category && (
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${catStyle(p.category)}`}>
                          {catLabel(p.category)}
                        </span>
                      )}
                      {wasUnread && (
                        <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-primary text-white">
                          חדש
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">
                      {p.author?.name && <>{p.author.name} · </>}
                      {timeAgo(p.publishedAt)}
                    </div>
                    <div
                      className="text-sm text-slate-700 leading-relaxed space-y-2 [&_p]:m-0 [&_strong]:font-bold [&_strong]:text-slate-900"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderBody(p.body)) }}
                    />
                  </div>
                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(p.id)}
                      className="size-7 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex items-center justify-center flex-shrink-0"
                      title="מחק פוסט"
                    >
                      <Icon name="delete" size={14} />
                    </button>
                  )}
                </div>
                {confirmDeleteId === p.id && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="text-slate-600">למחוק את הפוסט?</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-1 rounded hover:bg-slate-100 text-slate-600"
                      >
                        לא
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="px-2 py-1 rounded bg-red-600 text-white font-semibold"
                      >
                        מחק
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </Drawer>
  );
};

export default ChangelogDrawer;
